import styled from '@emotion/styled';
import {
  Button,
  Modal,
  SvgIcon,
  SvgNames,
  tokens,
  Typography,
  useSingletonMobileMediaQuery,
} from '@sunrun/experience-ui-components';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useRecentTimeClockEvent } from 'hooks/useRecentTimeClockEvent';
import { useUserProfile } from 'providers/UserProfile';
import { useHybridToken } from 'providers/Auth';
import {
  createTimeClockEvent,
  getTimeAttestationPrompts,
  TimeAttestationPrompt,
  WorkdayAttestation,
} from 'providers/Workday';
import {
  CLOCK_ACTION_EVENT_TYPE,
  CLOCK_ACTION_SUCCESS_MESSAGE,
  CLOCK_RECENT_EVENT_MODAL_TYPE,
  CLOCK_RECENT_EVENT_TITLE,
  ClockAction,
  WORKDAY_EVENT_TYPE,
  WORKDAY_URL,
  WorkHourType,
} from 'constants/workday';
import {
  formatEventDateTime,
  getMealBreakRemainingMinutes,
  SchedulerAgreementFooterNote,
  WorkdayErrorCopy,
  WorkdayLink,
} from 'helpers/workdayHelpers';
import {
  WORKDAY_HOURS__NAVIGATED_TO_WORKDAY,
  WORKDAY_HOURS__NAVIGATED_TO_WORKDAY_FROM_ERROR_MESSAGE,
} from 'constants/mixpanelEvents';
import { Mixpanel } from 'providers/Mixpanel';

type ModalStatus =
  | 'form'
  | 'loading'
  | 'prompts'
  | 'submitting'
  | 'success'
  | 'error';

type ClockWorkHoursModalProps = {
  onClose: () => void;
  onNotify: (type: 'success' | 'error', message: string) => void;
};

const ClockWorkHoursModal = ({
  onClose,
  onNotify,
}: ClockWorkHoursModalProps) => {
  const isMobile = useSingletonMobileMediaQuery();
  const { userProfile } = useUserProfile();
  const { hybridToken } = useHybridToken();

  const [selectedAction, setSelectedAction] = useState<ClockAction | null>(
    null,
  );
  const [workHourType, setWorkHourType] = useState<WorkHourType | null>(null);

  const [status, setStatus] = useState<ModalStatus>('form');
  const [prompts, setPrompts] = useState<TimeAttestationPrompt[]>([]);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [comments, setComments] = useState<Record<string, string>>({});
  const [eventDateTime, setEventDateTime] = useState<string>('');

  const employeeId = userProfile?.employeeId;
  const timezoneId = userProfile?.timezoneId;

  const {
    recentEventStatus,
    recentEventType,
    recentEventDateTime,
    isRecentEventError,
    isRecentEventLoading,
    refreshRecentEvent,
  } = useRecentTimeClockEvent(true);

  const isBusy = status === 'loading' || status === 'submitting';
  const isPromptPhase = status === 'prompts';
  const isDone = status === 'success';
  const isError = status === 'error';

  const bottomAnchorRef = useRef<HTMLDivElement>(null);
  const promptSectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isError) {
      bottomAnchorRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });
    }
  }, [isError]);

  useEffect(() => {
    if (isPromptPhase) {
      promptSectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [isPromptPhase]);

  const handleActionSelect = (action: ClockAction) => {
    setSelectedAction(action);
    setWorkHourType(null);
  };

  const handleCheckInSelect = (type: WorkHourType) => {
    setSelectedAction('clock-in');
    setWorkHourType(type);
  };

  const isCheckInSelected = (type: WorkHourType) =>
    selectedAction === 'clock-in' && workHourType === type;

  const handleWorkdayLinkClick = () => {
    Mixpanel.track(WORKDAY_HOURS__NAVIGATED_TO_WORKDAY, {
      employeeId,
      url: WORKDAY_URL,
      selectedAction,
      workHourType,
      recentEventStatus: status,
      recentEventType: recentEventType,
    });
  };

  const handleWorkdayErrorLinkClick = () => {
    Mixpanel.track(WORKDAY_HOURS__NAVIGATED_TO_WORKDAY_FROM_ERROR_MESSAGE, {
      employeeId,
      url: WORKDAY_URL,
      selectedAction,
      workHourType,
      recentEventStatus: recentEventStatus,
      recentEventType: recentEventType,
    });
  };

  const submitEvent = async (
    action: ClockAction,
    dateTime: string,
    attestations: WorkdayAttestation[],
  ) => {
    if (!timezoneId) return;

    setStatus('submitting');
    try {
      await createTimeClockEvent(hybridToken, {
        eventType: CLOCK_ACTION_EVENT_TYPE[action],
        dateTime,
        timeZone: timezoneId,
        ...(action === 'clock-in' && workHourType
          ? { timeEntryCode: workHourType }
          : {}),
        ...(attestations.length > 0 ? { attestations } : {}),
      });

      setStatus('success');
      onNotify('success', CLOCK_ACTION_SUCCESS_MESSAGE[action]);
      onClose();
      void refreshRecentEvent();
    } catch {
      setStatus('error');
      onNotify('error', "We couldn't record your hours. Please try again.");
    }
  };

  const handleSave = async () => {
    if (!selectedAction || !timezoneId) return;

    // Second click while answering prompts submits the attestations.
    if (isPromptPhase) {
      const attestations: WorkdayAttestation[] = prompts
        .filter((prompt) => answers[prompt.id])
        .map((prompt) => ({
          promptId: prompt.id,
          responseId: answers[prompt.id],
          comment: comments[prompt.id]?.trim() || undefined,
        }));
      await submitEvent(selectedAction, eventDateTime, attestations);
      return;
    }

    const dateTime = new Date().toISOString();
    setEventDateTime(dateTime);

    // Meal break never returns prompts, so go straight to creating the event.
    if (selectedAction === 'meal-break') {
      await submitEvent(selectedAction, dateTime, []);
      return;
    }

    setStatus('loading');
    try {
      const response = await getTimeAttestationPrompts(hybridToken, {
        eventType: CLOCK_ACTION_EVENT_TYPE[selectedAction],
        dateTime,
        timeZone: timezoneId,
      });
      const fetched = response?.data ?? [];

      if (fetched.length === 0) {
        // No prompts to answer; create the event immediately.
        await submitEvent(selectedAction, dateTime, []);
        return;
      }

      setPrompts(fetched);
      setStatus('prompts');
    } catch {
      setStatus('error');
      onNotify(
        'error',
        "We couldn't load the required questions. Please try again.",
      );
    }
  };

  const promptsUnanswered = useMemo(
    () =>
      prompts.some((prompt) => {
        const responseId = answers[prompt.id];
        if (prompt.required && !responseId) return true;
        const chosen = prompt.responses.find(
          (response) => response.id === responseId,
        );
        const needsComment = prompt.requiresComment || chosen?.requiresComment;
        if (responseId && needsComment && !comments[prompt.id]?.trim()) {
          return true;
        }
        return false;
      }),
    [prompts, answers, comments],
  );

  const isSaveDisabled =
    isBusy ||
    isDone ||
    isError ||
    selectedAction === null ||
    (selectedAction === 'clock-in' && workHourType === null) ||
    (isPromptPhase && promptsUnanswered);

  const modalEventType = recentEventType ?? WORKDAY_EVENT_TYPE.CLOCK_OUT;
  const modalTitle = CLOCK_RECENT_EVENT_TITLE[modalEventType];
  const modalType = CLOCK_RECENT_EVENT_MODAL_TYPE[modalEventType];

  const modalSubtitle = (() => {
    if (!recentEventDateTime) return undefined;

    const sinceCopy = `Since ${formatEventDateTime(recentEventDateTime)}`;
    if (recentEventType !== WORKDAY_EVENT_TYPE.MEAL_BREAK) return sinceCopy;

    const remainingMinutes = getMealBreakRemainingMinutes(recentEventDateTime);
    if (!remainingMinutes) return sinceCopy;

    const minuteLabel = remainingMinutes === 1 ? 'minute' : 'minutes';
    return `${sinceCopy}. You have ${remainingMinutes} ${minuteLabel} remaining on your meal time!`;
  })();

  const userNotCurrentlyClockedIn =
    recentEventType === WORKDAY_EVENT_TYPE.MEAL_BREAK ||
    recentEventType === WORKDAY_EVENT_TYPE.CLOCK_OUT;

  const userCurrentlyClockedIn =
    recentEventType === WORKDAY_EVENT_TYPE.CLOCK_IN;

  if (isRecentEventError) {
    return (
      <div onMouseDown={(e) => e.stopPropagation()}>
        <Modal
          customModalWidth={800}
          testId="clock-work-hours-modal"
          type="error"
          title="Something went wrong while loading clock work hours"
          onClose={onClose}
          leftFooterContent={<SchedulerAgreementFooterNote />}
          secondaryButton={{
            text: 'Cancel',
            onClick: onClose,
            testId: 'clock-work-hours-modal-cancel-button',
          }}
          primaryButton={{
            text: 'Refresh the page',
            onClick: () => window.location.reload(),
            testId: 'clock-work-hours-modal-refresh-button',
          }}>
          <Typography variant="p" testId="clock-work-hours-error">
            <WorkdayErrorCopy onWorkdayClick={handleWorkdayErrorLinkClick} />
          </Typography>
        </Modal>
      </div>
    );
  }

  return (
    <div onMouseDown={(e) => e.stopPropagation()}>
      <Modal
        isModalDataLoading={isRecentEventLoading}
        customModalWidth={800}
        testId="clock-work-hours-modal"
        type={modalType}
        title={modalTitle}
        subtitle={modalSubtitle}
        onClose={onClose}
        leftFooterContent={<SchedulerAgreementFooterNote />}
        primaryButton={{
          text: 'Save',
          onClick: handleSave,
          disabled: isSaveDisabled,
          isLoading: isBusy,
          testId: 'clock-work-hours-modal-save-button',
        }}
        secondaryButton={
          isError
            ? {
                text: 'Refresh',
                onClick: () => window.location.reload(),
                testId: 'clock-work-hours-modal-refresh-button',
              }
            : undefined
        }>
        <Typography variant="p" size={tokens.FONT_SIZE_3}>
          Remember to record your hours by checking in, checking out, or taking
          a meal break. To view the details of your logged hours, you can do it
          in{' '}
          <WorkdayLink
            href={WORKDAY_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleWorkdayLinkClick}>
            Workday
          </WorkdayLink>
          .
        </Typography>
        <ActionButtonsRow isMobile={isMobile}>
          {userNotCurrentlyClockedIn && (
            <CheckInOption isMobile={isMobile}>
              <ActionButton
                isMobile={isMobile}
                color={
                  isCheckInSelected('productive') ? 'primary' : 'secondary'
                }
                size="xs"
                disabled={isBusy || isPromptPhase || isDone}
                onClick={() => handleCheckInSelect('productive')}
                iconLeft={
                  <ActionIconChip backgroundColor={tokens.TINTS_SUCCESS_70}>
                    <SvgIcon
                      name={SvgNames.ClockCountdown}
                      height={20}
                      width={20}
                      color={tokens.BRAND_HERO_BLUE}
                    />
                  </ActionIconChip>
                }>
                Check-in productive hours
              </ActionButton>
              <CheckInDescription
                variant="p"
                size={12}
                color={tokens.TINTS_OFF_WHITE_20}>
                Sales-related work that counts toward commission
              </CheckInDescription>
            </CheckInOption>
          )}
          {userNotCurrentlyClockedIn && (
            <CheckInOption isMobile={isMobile}>
              <ActionButton
                isMobile={isMobile}
                color={
                  isCheckInSelected('non-productive') ? 'primary' : 'secondary'
                }
                size="xs"
                disabled={isBusy || isPromptPhase || isDone}
                onClick={() => handleCheckInSelect('non-productive')}
                iconLeft={
                  <ActionIconChip backgroundColor={tokens.TINTS_SUCCESS_70}>
                    <SvgIcon
                      name={SvgNames.ClockCountdown}
                      height={20}
                      width={20}
                      color={tokens.BRAND_HERO_BLUE}
                    />
                  </ActionIconChip>
                }>
                Check-in non-productive hours
              </ActionButton>
              <CheckInDescription
                variant="p"
                size={12}
                color={tokens.TINTS_OFF_WHITE_20}>
                Training, meetings, licensing, and other non-sales activities
              </CheckInDescription>
            </CheckInOption>
          )}
          {userCurrentlyClockedIn && (
            <ActionButton
              isMobile={isMobile}
              color={selectedAction === 'clock-out' ? 'primary' : 'secondary'}
              size="xs"
              disabled={isBusy || isPromptPhase || isDone}
              onClick={() => handleActionSelect('clock-out')}
              iconLeft={
                <ActionIconChip backgroundColor={tokens.ERROR_70}>
                  <SvgIcon
                    name={SvgNames.ClockCountdown}
                    height={20}
                    width={20}
                    color={tokens.BRAND_HERO_BLUE}
                  />
                </ActionIconChip>
              }>
              Check-out
            </ActionButton>
          )}
          {userCurrentlyClockedIn && (
            <ActionButton
              isMobile={isMobile}
              color={selectedAction === 'meal-break' ? 'primary' : 'secondary'}
              size="xs"
              disabled={isBusy || isPromptPhase || isDone}
              onClick={() => handleActionSelect('meal-break')}
              iconLeft={
                <ActionIconChip backgroundColor={tokens.TINTS_HERO_YELLOW_60}>
                  <SvgIcon
                    name={SvgNames.Coffee}
                    height={20}
                    width={20}
                    color={tokens.BRAND_HERO_BLUE}
                  />
                </ActionIconChip>
              }>
              Meal Break (30 min)
            </ActionButton>
          )}
        </ActionButtonsRow>
        {isPromptPhase &&
          prompts.map((prompt, index) => {
            const selectedResponseId = answers[prompt.id];
            const chosen = prompt.responses.find(
              (response) => response.id === selectedResponseId,
            );
            const commentRequired =
              prompt.requiresComment || chosen?.requiresComment;
            return (
              <PromptSection
                key={prompt.id}
                ref={index === 0 ? promptSectionRef : undefined}
                data-testid={`workday-prompt-${prompt.id}`}>
                <Typography variant="p">{prompt.descriptor}</Typography>
                {prompt.responses.map((response) => (
                  <PromptResponseButton
                    isMobile={isMobile}
                    key={response.id}
                    color={
                      selectedResponseId === response.id
                        ? 'primary'
                        : 'secondary'
                    }
                    size="xs"
                    onClick={() =>
                      setAnswers((prev) => ({
                        ...prev,
                        [prompt.id]: response.id,
                      }))
                    }>
                    {response.descriptor}
                  </PromptResponseButton>
                ))}
                <CommentLabel variant="p">
                  {commentRequired
                    ? 'Comments (required)'
                    : 'Comments (optional)'}
                </CommentLabel>
                <CommentTextArea
                  data-testid={`workday-prompt-comment-${prompt.id}`}
                  value={comments[prompt.id] ?? ''}
                  onChange={(event) =>
                    setComments((prev) => ({
                      ...prev,
                      [prompt.id]: event.target.value,
                    }))
                  }
                />
              </PromptSection>
            );
          })}
        {isError && (
          <ErrorMessage variant="p" testId="clock-work-hours-error">
            <WorkdayErrorCopy onWorkdayClick={handleWorkdayErrorLinkClick} />
          </ErrorMessage>
        )}
        <div ref={bottomAnchorRef} />
      </Modal>
    </div>
  );
};

const ActionButtonsRow = styled.div<{ isMobile: boolean }>`
  display: flex;
  gap: 16px;
  margin-top: 24px;
  justify-content: space-between;
  align-items: flex-start;

  & > button {
    flex: 1;
  }

  & > button:last-child {
    flex: 1.2;
  }

  ${({ isMobile }) =>
    isMobile &&
    `
    flex-direction: column;
  `}
`;

const ActionButton = styled(Button)<{ isMobile: boolean }>`
  align-items: center;
  justify-content: center;

  > span {
    margin: 0px !important;
  }
  ${({ isMobile }) =>
    isMobile &&
    `
    width: 100%;
  `}
`;

const ActionIconChip = styled.span<{ backgroundColor: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: ${({ backgroundColor }) => backgroundColor};
`;

const CheckInOption = styled.div<{ isMobile: boolean }>`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 4px;
  ${({ isMobile }) =>
    isMobile &&
    `
    width: 100%;
  `}
`;

const CheckInDescription = styled(Typography)`
  margin: 0;
`;

const PromptSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  margin-top: 24px;
`;

const PromptResponseButton = styled(Button)<{ isMobile: boolean }>`
  align-items: center;
  justify-content: center;
  align-self: flex-start;
  text-align: left;
  ${({ isMobile }) =>
    isMobile &&
    `
    padding: 6px 26px !important;
  `}
`;

const CommentLabel = styled(Typography)`
  margin-top: 8px;
`;

const CommentTextArea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  min-height: 64px;
  padding: 12px;
  border: 1px solid ${tokens.BORDER_LIGHT};
  border-radius: 8px;
  resize: vertical;
  font-family: inherit;
  font-size: 16px;
`;

const ErrorMessage = styled(Typography)`
  padding-top: 24px !important;
  color: ${tokens.ERROR_60};
`;

export { ClockWorkHoursModal };
